import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import styles from "./DecisionStatsMultipleChoiceItem.module.css";
import { nanoid } from "nanoid";

import ViewPdfButton from "ManagementTeam/Shared/ViewPdfButton";
import LockIcon from "ManagementTeam/Shared/LockIcon";
import PendingVoteButton from "ManagementTeam/Shared/PendingVoteButton";
import CircleCheckbox from "ManagementTeam/Shared/CircleCheckbox";
import CompleteIcon from "ManagementTeam/Shared/CompleteIcon";
import Toggler from "ManagementTeam/Shared/Toggler";
import Select from "ManagementTeam/Shared/Select";
import MultipleChoiceItem from "ManagementTeam/Shared/MultipleChoiceItem";
import Button from "shared/Button";
import Markdown from "ManagementTeam/Shared/Markdown";

import VoteCount from "../VoteCount";
import VotingRadioButtonList from "../VotingRadioButtonList";
import MultipleChoiceVotesList from "../MultipleChoiceVotesList";
import ApprovedIcon from "./Approved_Gray.svg";
import { ChevronRight } from "react-ikonate";
import { ChevronDown } from "react-ikonate";
import VotingResultsList from "../VotingResultsList";
import { useDecisions } from "../useDecisions";
import Auth from "shared/@auth";

/**
 * Interactive component. A button that allows the user to reset their vote on a decision item.
 */
export function DecisionStatsMultipleChoiceItem({
  assignee,
  agendaId = "",
  assignedType,
  title,
  isDraft,
  description,
  isCompleted,
  votingStatus,
  pdfName,
  onClickPdf,
  onEdit,
  onDelete,
  onMove,
  onPublish,
  displayResults,
  voting,
  totalVotes,
  diamonds,
  results,
  multipleChoiceOptions,
  onToggle,
  onChangeVotingStatus,
  multipleChoiceVoteMade,
  decision,
  handleProxyVote,
  readOnly,
  document,
  isHideInfo,
}) {
  const { hasPermission } = Auth.useContainer();
  const isOwner = hasPermission("management-team.owners");

  const id = nanoid();

  const [toggled, setToggled] = useState(displayResults);
  const [votingClosedToggled, setVotingClosedToggled] = useState(false);
  const [checked, setChecked] = useState(isCompleted);
  const [showResultsList, setShowResultsList] = useState();
  const [showToggler, setShowToggler] = useState(voting === "closed");
  const [showLock, setShowLock] = useState(voting === "closed");
  const [votingState, setVotingState] = useState(voting);
  const [currentVotes, setCurrentVotes] = useState([]);

  const handleVotes = (itemPosition) => {
    if (currentVotes.includes(itemPosition)) {
      setCurrentVotes((prev) => currentVotes.filter((e) => e !== itemPosition));
    } else {
      setCurrentVotes((prev) => [...currentVotes, itemPosition]);
    }
  };

  // useMemo(() => {
  //   console.log(currentVotes);
  // }, [currentVotes]);

  const boxColor = (value) => {
    if (value === "Diamond") {
      return styles.diamond;
    } else if (value === "WWG") {
      return styles.wwg;
    } else if (value === "Attorney") {
      return styles.attorney;
    }
  };
  const handleToggle = () => {
    setToggled(!toggled);
    onToggle(!toggled);
  };

  const handleVotingClosedToggle = () => {
    setVotingClosedToggled(!votingClosedToggled);
  };

  const handleVoting = (value) => {
    setVotingState(value);
    if (value === "closed") {
      setShowToggler(true);
      setShowLock(true);
    } else {
      setShowToggler(false);
      setShowLock(false);
    }
    onChangeVotingStatus(value?.toLowerCase());
  };

  const handleChange = (value) => {
    if (value === "Edit") {
      onEdit();
    } else if (value === "Delete") {
      onDelete();
    } else if (value === "Move/Duplicate") {
      onMove();
    }
  };

  return (
    <>
      <div className={styles.flex}>
        <div className={styles.container}>
          <div className={styles.decisionContainer}>
            <div className={styles.topicContainer}>
              <div
                className={classNames(
                  styles.boxTypeLabel,
                  boxColor(assignedType)
                )}
              >
                {checked ? (
                  <img
                    src={ApprovedIcon}
                    alt="Approved"
                    onClick={() => {
                      if (!readOnly) setChecked(!checked);
                    }}
                    className={styles.image}
                  />
                ) : null}
              </div>
              <div className={styles.descriptionContainer}>
                <div className={styles.infoAssignee}>{assignee}</div>
                <div>{title}</div>
                <div className={styles.infoDesc}>
                  <Markdown content={description} />
                </div>
                {multipleChoiceOptions.map((option, index) => {
                  return (
                    <div
                      className={styles.multipleChoiceItem}
                      onClick={() => {
                        votingState !== "closed" &&
                          !votingClosedToggled &&
                          votingState !== "off" &&
                          handleVotes(option.itemPosition);
                      }}
                      key={index}
                    >
                      <MultipleChoiceItem
                        isSelected={currentVotes.includes(option.itemPosition)}
                        itemPosition={option.itemPosition}
                        itemTitle={option.itemTitle}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            <div className={styles.votingContainer}>
              <div className={classNames(styles.radioList)}>
                <VotingRadioButtonList
                  onClick={(value) => handleVoting(value)}
                  status={votingState}
                  readOnly={readOnly}
                />
              </div>
              {showToggler ? (
                <div className={styles.togglerSecond}>
                  <div className={styles.votingSubtitle}>Display Results</div>
                  <Toggler
                    onChange={handleToggle}
                    toggled={toggled}
                    color={"#39B54A"}
                    key={id}
                    readOnly={readOnly}
                    disabled={readOnly}
                  />
                </div>
              ) : null}
            </div>

            {!isHideInfo && (
              <div className={styles.resultsContainer}>
                <>
                  <div className={styles.overview}>
                    {votingStatus === "Complete" ? (
                      <div className={styles.statusInfo}>
                        <div className={styles.lockIcon}>
                          {showLock && <LockIcon />}
                        </div>
                        <CompleteIcon isLocked={true} />
                        <div className={styles.completeText}>Complete</div>
                      </div>
                    ) : null}

                    {votingStatus === "Pending" ? (
                      <div className={styles.statusInfo}>
                        <div className={styles.lockIcon}>
                          {showLock && <LockIcon />}
                        </div>
                        <PendingVoteButton isLocked={true} />
                        <div className={styles.pendingText}>Pending</div>
                      </div>
                    ) : null}

                    {votingStatus === "Upcoming" ? (
                      <div className={styles.statusInfo}>
                        <div className={styles.lockIcon}>
                          {showLock && <LockIcon />}
                        </div>
                        <PendingVoteButton isGrayed={true} isLocked={true} />
                        <div className={styles.pendingText}>Upcoming</div>
                      </div>
                    ) : null}

                    <div>
                      <VoteCount
                        votesMade={decision.voteCount}
                        totalVotes={decision.attendanceCount}
                      />
                    </div>
                  </div>
                  <div className={styles.multipleChoiceVotesList}>
                    <MultipleChoiceVotesList results={results} />
                  </div>
                </>
              </div>
            )}
            <div className={styles.documentsContainer}>
              {document && (
                <>
                  <ViewPdfButton onClick={onClickPdf} />
                  <div>{pdfName}</div>
                </>
              )}
            </div>
            <div className={styles.completeContainer}>
              <CircleCheckbox
                checked={checked}
                onChange={() => setChecked(!checked)}
                readOnly={readOnly}
              />
            </div>
          </div>
          {showResultsList ? (
            <div>
              <div
                className={styles.showResultsBar}
                onClick={() => setShowResultsList(!showResultsList)}
              >
                <div className={styles.barTitle}>
                  COLLAPSE RESULTS <ChevronDown fontSize="23px" />
                </div>
              </div>
              <div className={styles.votingResultsList}>
                <VotingResultsList
                  readOnly={readOnly}
                  diamonds={diamonds}
                  multipleChoiceItems={multipleChoiceOptions}
                  onSubmit={(e) => handleProxyVote(e, agendaId)}
                  isHideInfo={isHideInfo}
                />
              </div>
            </div>
          ) : isOwner ? (
            <div
              className={styles.showResultsBar}
              onClick={() => setShowResultsList(!showResultsList)}
            >
              <div className={styles.barTitle}>
                EXPAND RESULTS <ChevronRight fontSize="23px" />
              </div>
            </div>
          ) : (
            <></>
          )}
        </div>
        {!readOnly && (
          <div className={styles.sideContainer}>
            <div className={styles.selectContainer}>
              <Select
                options={["Edit", "Delete", "Move/Duplicate"]}
                onChange={handleChange}
                placeholder={"Options"}
                className={styles.select}
              />
            </div>
            <Button
              purpose="save"
              children="Publish"
              className={styles.publishButton}
              onClick={() => {
                onPublish();
              }}
              style={{
                visibility: !readOnly && isDraft ? "visible" : "hidden",
              }}
              disabled={readOnly || !isDraft}
            />
          </div>
        )}
      </div>
    </>
  );
}

DecisionStatsMultipleChoiceItem.propTypes = {
  // Assignee of the decision (Bold Text)
  assignee: PropTypes.string,

  // The type of assigned item, either "Diamond”, "WWG”, or "Attorney”.  Left Brown Box (#7F6A45) is
  // Diamond, Left Teal Box (#91B6BB) is WWG, and Left Gray Box (#ADB7C0) is Attorney.
  assignedType: PropTypes.string,

  // Title of the decision item (Regular Text)
  title: PropTypes.string,

  // Description of the details of the decision item (Italic Text)
  description: PropTypes.string,

  // Boolean that holds if the item is completed. A checkmark will show on the left and right if it is.
  // User can click on either check mark to switch between true/false
  isCompleted: PropTypes.bool,

  // The current voting status of the decision item (Passed, Failed, Pending, Upcoming, Complete or Tie vote)
  votingStatus: PropTypes.string,

  // A string that holds the name of the PDF file for the View PDF Button
  pdfName: PropTypes.string,

  // Function that’s used to view the PDF when clicking the View PDF Button
  onClickPdf: PropTypes.func,

  // Function called when edit is clicked
  onEdit: PropTypes.func,

  // Functin called when delete is clicked
  onDelete: PropTypes.func,

  // Function called when Move/Duplicate is clicked
  onMove: PropTypes.func,

  // Function called when published is clicked
  onPublish: PropTypes.func,

  // Decided if results will be displayed. Toggler in the voting section.
  displayResults: PropTypes.bool,

  // Sting that is off on or closed to be displayed in the voting section
  voting: PropTypes.string,

  // Number of total votes
  totalVotes: PropTypes.number,

  // array of strongs that hold the mc options (if item is mc)
  multpleChoiceOptions: PropTypes.arrayOf(String),

  // Array of diamonds to display in the results section
  diamonds: PropTypes.array,

  decision: PropTypes.any,
};

DecisionStatsMultipleChoiceItem.defaultProps = {
  diamonds: [],
  description: "",
  multipleChoiceOptions: [],
};
